import { FunctionComponent } from 'react';
import { action, computed, observable, makeObservable } from 'mobx';
import ModalWindow, { ConfirmModalType, DEFAULT_MODAL_ID, IModalEvent } from 'modal-windows/models/modalWindow';
import EmptyComponent from '@components/EmptyComponent';

export interface IConfirmModal {
    text: string;
    type: ConfirmModalType;
}

export class ModalService {
    @observable
    private _openModalStack: ModalWindow<any>[] = [];

    private get _currentModalWindow(): ModalWindow<any> {
        return this._openModalStack[this._openModalStack.length - 1];
    }

    @computed
    public get visible(): boolean {
        return this._currentModalWindow.visible!;
    }

    @computed
    public get template(): FunctionComponent {
        return this._currentModalWindow.template!;
    }

    @computed
    public get data(): any {
        return this._currentModalWindow.data;
    }

    @computed
    public get currentModalId(): number {
        return this._currentModalWindow.id!;
    }

    @computed
    public get confirmationType(): ConfirmModalType {
        const { type = null } = this._currentModalWindow.data;

        return type;
    }

    @computed
    public get confirmationText(): ModalWindow<IConfirmModal> {
        const { text = null } = this._currentModalWindow.data;

        return text;
    }

    @computed
    public get isAFreeState(): boolean {
        return !this._currentModalWindow.modalEvent && !this.visible;
    }

    constructor() {
        makeObservable(this);
        this._openModalStack.push(new ModalWindow<null>(false, EmptyComponent, null, null));
    }

    @action
    private _removeModalFromStack(modalId: number): void {
        this._openModalStack = this._openModalStack.filter((item: ModalWindow<any>) => item.id !== modalId);
    }

    private _removeModalFromStackById(id: number): void {
        this._removeModalFromStack(id);
    }

    private _removeModalFromStackByCurrentModalId(id?: number): void {
        this._removeModalFromStack(id || this.currentModalId);
    }

    @action
    private _eventSubscription<T>(template: FunctionComponent, data: T): Promise<boolean> {
        return new Promise((resolve, reject) =>
            this._openModalStack.push(new ModalWindow<T>(true, template, { resolve, reject } as IModalEvent, data)),
        );
    }

    @action
    public showModal<T>(template: FunctionComponent, data: T | null = null): Promise<boolean> {
        return this._eventSubscription<T | null>(template, data);
    }

    // @action
    // public showConfirmModal(text: string, type: ConfirmModalType): Promise<boolean> {
    //     return this._eventSubscription<{ text: string; type: ConfirmModalType }>(ConfirmModal, { text, type });
    // }

    @action
    closeModalById(id: number): void {
        const modal = this._openModalStack.find((modal: ModalWindow<any>) => modal.id === id);

        if (modal) {
            modal.cancel();
            this._removeModalFromStackById(id);
        } else {
            throw new Error('Unable to close undefined modal.');
        }
    }

    @action
    closePrevModal(): void {
        const modal = this._openModalStack[this._openModalStack.length - 2];

        if (modal) {
            modal.cancel();
            this._removeModalFromStackById(modal.id as any);
        } else {
            throw new Error('Unable to close prev modal.');
        }
    }

    @action
    public cancel(): void {
        this._currentModalWindow.visible = false;
        this._currentModalWindow.cancel();
        this._removeModalFromStackByCurrentModalId();
    }

    @action
    public success(): void {
        this._currentModalWindow.visible = false;
        this._currentModalWindow.success();
        this._removeModalFromStackByCurrentModalId();
    }

    @action
    public clearModalStack(): void {
        this._openModalStack = this._openModalStack.filter((modal: ModalWindow<any>) => modal.id === DEFAULT_MODAL_ID);
    }
}
